import { isValid, parseISO } from "date-fns";
import { channelTypes } from "@/components/program-manager/sessions/constants.js";
import { API_ENTITY_INFORMATION } from "@/constants";

/**
 * True when session/order payload is Exchange (channel 4). Supports `channel_id`, nested `channel.id`,
 * and string/number variants from APIs.
 * @param {object | null | undefined} programSession
 * @returns {boolean}
 */
export const isExchangeChannel = (programSession) => {
  if (!programSession) {
    return false;
  }
  const rawId = programSession.channel_id ?? programSession.channel?.id;
  if (rawId) {
    const n = Number(rawId);
    if (!Number.isNaN(n) && n === channelTypes.EXCHANGE) {
      return true;
    }
    if (String(rawId).trim() === String(channelTypes.EXCHANGE)) {
      return true;
    }
  }
  const name = programSession.channel?.name;
  if (typeof name === "string" && name.toLowerCase() === "exchange") {
    return true;
  }
  return false;
};

const validateContractDateType = (startDate, endDate) => {
  // Validate that dates have right Type
  return typeof startDate === "string" && typeof endDate === "string";
};

const setTime = (setAsEndDay = false, dateToChange = new Date()) => {
  // Sets time in a date
  if (setAsEndDay) {
    dateToChange.setHours(23);
    dateToChange.setMinutes(59);
    dateToChange.setSeconds(59); // xxxx:xx:xx 23:59:59
  } else {
    dateToChange.setHours(0);
    dateToChange.setMinutes(0);
    dateToChange.setSeconds(0); // xxxx:xx:xx 00:00:00
  }
  return dateToChange;
};

const betweenDates = (startDate, endDate) => {
  // Check if curret date is between Start and End date
  if (!(startDate instanceof Date) || !(endDate instanceof Date) || !isValid(startDate) || !isValid(endDate)) {
    return false;
  }
  const currentTime = new Date().getTime();
  const startTime = setTime(false, startDate).getTime();
  const endTime = setTime(true, endDate).getTime();
  return currentTime >= startTime && currentTime <= endTime;
};

/**
 * @param {unknown[]} contracts
 * @param {object} [programSession]
 * @param {GetApplicableContractOptions} [options]
 */
const getApplicableContract = (contracts, programSession, options = {}) => {
  const isInvalidInput = !Array.isArray(contracts) || contracts.length === 0 || !programSession;

  // spec: docs/billing-rules.md §3 — deliberate: a tenant-owned session bills through its own
  // institution, never through an API agreement. Not a missing case.
  const isApiOwnedSession = programSession?.owner_entity_id === API_ENTITY_INFORMATION.id;

  if (isInvalidInput || !isApiOwnedSession) {
    return;
  }

  // Filter contracts that are between start and end dates and active
  const validContracts = contracts.filter((contract) => {
    return (
      validateContractDateType(contract.contract_start_date, contract.contract_end_date) &&
      betweenDates(parseISO(contract.contract_start_date), parseISO(contract.contract_end_date)) &&
      contract.status === "Activated"
    );
  });

  if (isExchangeChannel(programSession)) {
    const billingContractId = options.billingContractId;
    if (billingContractId) {
      const matched = validContracts.find((contract) => contract.contract_number === billingContractId);
      return matched;
    }
    return;
  }

  // Gather terms of program session
  if (!programSession.terms || !Array.isArray(programSession.terms) || programSession.terms.length === 0) {
    return undefined;
  }
  const termsFromSession = programSession.terms.map((term) => term.name);

  // Check if program session has internship
  const hasInternship = programSession.session_types.some((type) => type.id === 2);

  // Check if program session has study abroad
  const hasStudy = programSession.session_types.some((type) => type.id === 1);

  for (const contract of validContracts) {
    if (contract.programs.some((program) => program.salesforce_program_session_id === programSession.salesforce_id)) {
      return contract;
    }

    for (const contractTerm of contract.terms) {
      if (termsFromSession.includes(contractTerm)) {
        if (hasStudy && contract.all_hosts) {
          return contract;
        } else if (hasStudy) {
          const hostsFromContract = contract.hosts.map((host) => host.salesforce_host_university_id);
          const hasHost = programSession.session_hosting_institutions.some((host) =>
            hostsFromContract.includes(host.entity_id),
          );
          if (hasHost) return contract;
        } else if (hasInternship && contract.all_sites) {
          return contract;
        } else if (hasInternship) {
          const sitesFromContract = contract.sites.map((site) => site.name);
          const hasSite = programSession.session_travel_details.some((travel_details) =>
            sitesFromContract.includes(travel_details.arrival_city.city),
          );

          if (hasSite) return contract;
        }
      }
    }
  }

  return undefined;
};

export default getApplicableContract;
